import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { 
  ArrowLeft, 
  Users, 
  Star, 
  Clock, 
  Edit, 
  Trash2, 
  Play, 
  FileText, 
  Link as LinkIcon, 
  Plus, 
  CheckCircle, 
  Image as ImageIcon, // Renamed to avoid conflict with Link
  File as FileDocIcon // Renamed to avoid conflict with FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import {api, API_BASE_URL} from '../../api';

const ProfessorCourseView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    loadCourse();
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/professor/courses/${id}`);

      if (response.data) {
        const data = response.data.data; // Access data.data as per backend response structure
        setCourse(data);
        
        // Expand the first section by default
        if (data.sections && data.sections.length > 0) {
          setExpandedSections([data.sections[0]._id]);
        }
      } else {
        toast.error('Erreur lors du chargement du cours');
        navigate('/professor/courses');
      }
    } catch (error) {
      console.error('Load course error:', error);
      toast.error('Erreur lors du chargement du cours');
      navigate('/professor/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    try {
      const response = await api.delete(`/professor/courses/${id}`);

      if (response.data) {
        toast.success('Cours supprimé avec succès');
        navigate('/professor/courses');
      } else {
        toast.error('Erreur lors de la suppression du cours');
      }
    } catch (error) {
      console.error('Delete course error:', error);
      toast.error('Erreur lors de la suppression du cours');
    }
  };

  const toggleSectionExpand = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video': return <Play className="h-4 w-4 text-red-600" />;
      case 'pdf': return <FileText className="h-4 w-4 text-[#FFB639]" />;
      case 'text': return <FileText className="h-4 w-4 text-gray-600" />;
      case 'link': return <LinkIcon className="h-4 w-4 text-[#FFB639]" />;
      case 'image': return <ImageIcon className="h-4 w-4 text-[#FFB639]" />;
      case 'document': return <FileDocIcon className="h-4 w-4 text-[#FFB639]" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'draft': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'pending': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published': return 'Publié';
      case 'draft': return 'Brouillon';
      case 'pending': return 'En attente';
      case 'archived': return 'Archivé';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FileText className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Cours non trouvé</h3>
          <p className="text-gray-500 mb-4">
            Le cours que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <button
            onClick={() => navigate('/professor/courses')}
            className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            Retour à la liste des cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/professor/courses')}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/professor/courses/${id}/edit`)}
              className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </button>
            <button
              onClick={() => navigate(`/professor/courses/${id}/assign`)}
              className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              <Users className="h-4 w-4 mr-2" />
              Assigner
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </button>
          </div>
        </div>

        {/* Course Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="md:col-span-1">
              <img
                src={course.thumbnail ? `${API_BASE_URL}${course.thumbnail}` : 'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'}
                alt={course.title}
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'; // Fallback to placeholder
                }}
              />
            </div>
            <div className="md:col-span-2 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(course.status)}`}>
                  {getStatusLabel(course.status)}
                </span>
                <span className="text-sm text-gray-500">
                  Créé le {new Date(course.createdAt).toLocaleDateString('fr-FR')}
                </span>
              </div>

              <p className="text-gray-700 mb-6">{course.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-[#FFB639] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Étudiants</div>
                      <div className="font-semibold">{course.students?.length || 0}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-[#FFB639] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Durée</div>
                      <div className="font-semibold">{course.estimatedDuration || course.totalDuration || 0}h</div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-[#FFB639] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Modules</div>
                      <div className="font-semibold">
                        {course.sections?.reduce((total: number, section: any) => total + (section.modules?.length || 0), 0) || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-[#FFB639] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Note</div>
                      <div className="font-semibold">
                        {course.rating?.average > 0 ? course.rating.average.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <span className="text-gray-600 font-medium">Catégorie:</span>
                  <span className="ml-2">{course.category?.name || course.category || 'Non spécifiée'}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Niveau:</span>
                  <span className="ml-2">{course.level}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Prix:</span>
                  <span className="ml-2">{course.price}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Contenu du cours</h2>
          
          {course.sections && course.sections.length > 0 ? (
            <div className="space-y-4">
              {course.sections.map((section: any) => (
                <div key={section._id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className="bg-gray-50 p-4 cursor-pointer flex justify-between items-center"
                    onClick={() => toggleSectionExpand(section._id)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Section {section.order}: {section.title}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {section.modules?.length || 0} modules
                      </span>
                      <button className="text-gray-400 hover:text-gray-600">
                        {expandedSections.includes(section._id) ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {expandedSections.includes(section._id) && (
                    <div className="p-4 border-t border-gray-200">
                      {section.modules && section.modules.length > 0 ? (
                        <div className="space-y-2">
                          {section.modules.map((module: any) => (
                            <div key={module._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                              <div className="mr-3">
                                {getModuleIcon(module.type)}
                              </div>
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {module.title}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({module.duration}min)
                                </span>
                                {module.description && (
                                  <p className="text-xs text-gray-600 mt-1">{module.description}</p>
                                )}
                                {module.type === 'text' && module.content && (
                                  <p className="text-xs text-gray-600 mt-1">Contenu: {module.content.substring(0, 100)}{module.content.length > 100 ? '...' : ''}</p>
                                )}
                                {module.type === 'link' && module.externalUrl && (
                                  <p className="text-xs text-gray-600 mt-1">Lien: <a href={module.externalUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB639] hover:underline">{module.externalUrl}</a></p>
                                )}
                                {module.fileUrl && (module.type === 'video' || module.type === 'pdf' || module.type === 'image' || module.type === 'document') && (
                                  <p className="text-xs text-gray-600 mt-1">Fichier: <a href={`${API_BASE_URL}${module.fileUrl}`} target="_blank" rel="noopener noreferrer" className="text-[#FFB639] hover:underline">{module.fileUrl.split('/').pop()}</a></p>
                                )}
                              </div>
                              <button
                                onClick={() => navigate(`/professor/courses/${course._id}/section/${section._id}/module/${module._id}`)}
                                className="ml-auto px-3 py-1 bg-[#FFB639] text-white text-xs rounded-md hover:bg-[#e5a333] transition-colors"
                              >
                                Lecture
                              </button>
                              <button
                                onClick={() => navigate(`/professor/courses/${course._id}/section/${section._id}/module/${module._id}/edit`)}
                                className="ml-2 px-3 py-1 bg-[#FFB639] text-white text-xs rounded-md hover:bg-[#e5a333] transition-colors"
                              >
                                Modifier
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          Aucun module dans cette section
                        </div>
                      )}
                      
                      {section.quiz && (
                        <div className="mt-4 p-3 bg-[#FFB639]/10 rounded-lg border border-[#FFB639]">
                          <div className="flex items-center">
                            <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                            <div>
                              <span className="font-medium text-gray-900">Quiz: {section.quiz.title}</span>
                              <div className="text-xs text-[#FFB639]">
                                Évaluation de section
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <FileText className="h-16 w-16 mx-auto" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Aucun contenu ajouté
              </h4>
              <p className="text-gray-500 mb-4">
                Commencez par ajouter des sections et des modules à votre cours
              </p>
              <button 
                onClick={() => navigate(`/professor/courses/${id}/edit`)}
                className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
              >
                Ajouter du contenu
              </button>
            </div>
          )}
        </div>

        {/* Enrolled Students */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Étudiants inscrits</h2>
            <button
              onClick={() => navigate(`/professor/courses/${id}/assign`)}
              className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Assigner des étudiants
            </button>
          </div>
          
          {course.students && course.students.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Étudiant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date d'inscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progression
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière activité
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {course.students.map((student: any) => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-[#FFB639] rounded-full flex items-center justify-center text-white">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {/* You might need to fetch enrollment date and progress from a separate model if not directly on student */}
                        N/A
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        N/A
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        N/A
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <Users className="h-16 w-16 mx-auto" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Aucun étudiant inscrit
              </h4>
              <p className="text-gray-500 mb-4">
                Assignez des étudiants à ce cours pour commencer
              </p>
              <button
                onClick={() => navigate(`/professor/courses/${id}/assign`)}
                className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
              >
                Assigner des étudiants
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700">
                  Êtes-vous sûr de vouloir supprimer ce cours ? Cette action est irréversible et supprimera également tous les modules, quiz et inscriptions associés.
                </p>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteCourse}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfessorCourseView;